
import { defineComponent, nextTick, reactive, ref, toRefs, watch, computed, createVNode, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import store from '@/store'
import { zh_tran, objTran } from '@/utils/lang.js'
import {
  PlusOutlined,
  BoldOutlined,
  CalendarOutlined,
  PictureOutlined,
  DoubleLeftOutlined,
  LeftOutlined,
  RightOutlined,
  DoubleRightOutlined,
  SmileOutlined
} from '@ant-design/icons-vue'
import { Modal, message } from 'ant-design-vue'
import optionalshares from '@/components/optionalshares.vue'
import globalsets from '@/utils/globalsets'
import allApi from '@/api/allApi'
import Divedit from '@/views/stocknotes/divedit.vue'
import stocknotesApi from '@/api/stocknotesApi'
import stock from '@/utils/stock'
import Nodata from '@/components/nodata.vue'
import type { stockType, note, dateOption, pageDataType } from './handle'
import emojis from './emojis'
import { emojiArray, emojiArrayV2 } from './transform'

const project = require('@/views/stocknotes/project.json')

export default defineComponent({
  components: {
    PlusOutlined,
    BoldOutlined,
    CalendarOutlined,
    PictureOutlined,
    optionalshares,
    Divedit,
    Nodata,
    DoubleLeftOutlined,
    LeftOutlined,
    RightOutlined,
    DoubleRightOutlined,
    SmileOutlined
  },
  setup(props: any) {
    const route = useRoute()
    const diveditRef = ref()
    const today = Number((Date.now() / 1000 + '').split('.')[0])

    // 默认参数，路由没有时使用
    const defaultUid = (route.query.uid as string)
    const defaultProjectID = (route.query.ProjectID as string)
    const defaultProjectName = (route.query.ProjectName as string)

    // 判断是否是方德智投项目
    const isFangDeZhiTou = computed(() => defaultProjectName === '方德智投')

    // 语言切换：根据路由参数 Accept-Language 判断，zh-Hant 为繁体，默认 zh-Hans 为简体
    const lang = (route.query['Accept-Language'] as string) || 'zh-Hans'
    const isTraditional = lang === 'zh-Hant'

    const pageData = reactive({
      radiovalue: 4,
      searchText: '',
      text: '',
      getCalendarContainer: (e: HTMLElement) => e.parentNode,
      token: '',
      noteslist: [] as any[],
      currentProject: {} as any,
      content: '',
      options: [] as any[],
      showSelect: false,
      showDateSelect: false,
      showEmojiSelect: false,
      activeEmojiTab: 'basic',
      currentNote: {
        index: -1,
        Title: '',
        Content: '',
        ID: '',
        PubTime: 0
      } as any,
      selectionIndex: 0,
      anchorNode: {} as any,
      selection: {} as any,
      theme: (route.query.theme as string) || 'light',
      emptynotetitle: isTraditional ? '<新建個股筆記>' : '<新建个股笔记>',
      switchtype: route.query.switchtype as string,
      tradeDays: [] as string[],
      selectedDate: window.moment() as any,
      loading: false,
      pageLoaded: false,
      isFirstLoad: true,
      dateChanged: false,
      draftNote: null as any, // 新增草稿状态
      lastValidDate: window.moment() as any, // 记录上一次有效日期
      originalTitle: '',
      originalContent: '',
      originalDate: ''
    })

    pageData.currentProject = project.filter((item: any) => item.softNameAbbr === route.query.softNameAbbr)[0]

    // 初始化时设置语言模式
    if (isTraditional) {
      zh_tran('t')
    }

    // 文字转换方法，使用 lang.js 的 tran 方法
    function tran(text: string) {
      if (isTraditional) {
        return (text as any).tran()
      }
      return text
    }

    function switchLanguage() {
      if (isTraditional) {
        // 切换为繁体
        zh_tran('t')
      }
    }

    async function gettoken() {
      pageData.token = await globalsets.getToken()
      if (route.query.market && route.query.code) {
        pageData.radiovalue = 4
        // fetchTradeDays(route.query.market as string, route.query.code as string)
      } else {
        pageData.radiovalue = 1
      }

      if (route.query.TradeDay) {
        const t = route.query.TradeDay as string
        if (t.includes('-')) {
          pageData.selectedDate = window.moment(t)
        } else {
          const timestamp = Number(t)
          if (!isNaN(timestamp)) {
            pageData.selectedDate = window.moment(timestamp * 1000)
          }
        }
        // 初始化 lastValidDate
        if (pageData.selectedDate && !pageData.selectedDate.isAfter(window.moment(), 'day')) {
          pageData.lastValidDate = pageData.selectedDate.clone()
        }
      }

      // token 获取完成后调用 getlist（getlist 内部会处理 showTradeDay 参数）
      if (route.query.token) {
        getlist()
      }
    }
    gettoken()

    // 页面加载完成后切换语言
    onMounted(() => {
      nextTick(() => {
        switchLanguage()


      })
    })

    // 监听 loading 状态变化，数据加载完成后重新切换语言
    watch(() => pageData.loading, (newVal, oldVal) => {
      if (oldVal === true && newVal === false) {
        // 数据加载完成后，延迟执行确保 DOM 完全渲染
        nextTick(() => {
          setTimeout(() => {
            switchLanguage()
          }, 100)
        })
      }
    })

    // 监听笔记列表变化，重新切换语言
    watch(() => pageData.noteslist.length, () => {
      nextTick(() => {
        setTimeout(() => {
          switchLanguage()
        }, 50)
      })
    })

    function fetchTradeDays(exch: string, inst: string) {
      if (!exch || !inst) return
      const data: any = {
        data: {
          Uid: defaultUid,
          ProjectID: pageData.currentProject.ProjectID,
          Exch: exch,
          Inst: inst
        },
        headers: { token: pageData.token }
      }

      stocknotesApi.getMsgboardNoteTradeDays(data).then((res: any) => {
        const { Data } = res
        if (Data && Array.isArray(Data)) {
          // Convert timestamps to YYYY-MM-DD strings
          pageData.tradeDays = Data.map((t: number) => window.moment(t * 1000).format('YYYY-MM-DD'))
        } else {
          pageData.tradeDays = []
        }
      })
    }

    const holidayCache = reactive<Record<number, { holidays: string[], workdays: string[] }>>({})

    // 静态导入节假日数据（确保打包时包含）
    const holidayDataModules: Record<number, any> = {}
    try {
      // @ts-ignore
      holidayDataModules[2024] = require('chinese-holidays/data/2024.json')
      // @ts-ignore
      holidayDataModules[2025] = require('chinese-holidays/data/2025.json')
      // @ts-ignore
      holidayDataModules[2026] = require('chinese-holidays/data/2026.json')
    } catch (e) {
      console.warn('Failed to load chinese-holidays data:', e)
    }

    function parseHolidayData(data: any) {
      const holidays: string[] = []
      const workdays: string[] = []

      if (Array.isArray(data)) {
        data.forEach((item: any) => {
          if (item.range) {
            const start = item.range[0]
            const end = item.range[1] || start
            const current = window.moment(start)
            const finish = window.moment(end)

            while (current.isSameOrBefore(finish)) {
              const dateStr = current.format('YYYY-MM-DD')
              if (item.type === 'holiday') {
                holidays.push(dateStr)
              } else if (item.type === 'workingday') {
                workdays.push(dateStr)
              }
              current.add(1, 'days')
            }
          }
        })
      }

      return { holidays, workdays }
    }

    function getYearData(year: number) {
      if (holidayCache[year]) return holidayCache[year]

      let holidays: string[] = []
      let workdays: string[] = []

      // 使用静态导入的数据
      if (holidayDataModules[year]) {
        console.log(`使用静态导入的数据1`)
        const parsed = parseHolidayData(holidayDataModules[year])
        holidays = parsed.holidays
        workdays = parsed.workdays
      } else {
        // 尝试动态加载其他年份
        try {
          // @ts-ignore
          const data = require(`chinese-holidays/data/${year}.json`)
          const parsed = parseHolidayData(data)
          holidays = parsed.holidays
          workdays = parsed.workdays
          console.log(`使用静态导入的数据2`)
        } catch (e) {
          console.warn(`No holiday data for ${year}, using weekend-only mode`)
        }
      }

      holidayCache[year] = { holidays, workdays }
      console.log(`使用静态导入的数据3`)
      return holidayCache[year]
    }

    function handleDateDisabled(current: any) {
      if (!current) return false

      // 只禁用未来日期（今天之后的日期不可选）
      if (current.isAfter(window.moment(), 'day')) {
        return true
      }

      return false
    }

    function isRestDay(current: any) {
      if (!current) return false
      const dateStr = current.format('YYYY-MM-DD')
      const year = current.year()
      const { holidays } = getYearData(year)

      // 节假日
      if (holidays.includes(dateStr)) {
        return true
      }

      // 周末
      const day = current.day()
      if (day === 0 || day === 6) {
        return true
      }

      return false
    }

    function handleTabChange(val: number) {
      checkUnsavedChange(() => {
        // 切换标签时，如果当前笔记有未保存的内容（特别是新建的笔记），将其暂存起来
        if (pageData.currentNote && !pageData.currentNote.ID) {
          const hasContent = (pageData.currentNote.Title && pageData.currentNote.Title.trim()) ||
            (pageData.content && (stripHtml(pageData.content).trim() || pageData.content.includes('<img')));

          if (hasContent) {
            // 将未保存的新建笔记暂存到 localStroage 或 component 状态中
            // 这里简单起见，我们直接把它保留在 pageData.noteslist 的第一项，不刷新列表
            // 但切换 tab 通常会调用 getlist 刷新数据，所以我们需要特殊处理

            // 方案：暂存这个 draft 对象
            pageData.draftNote = {
              ...pageData.currentNote,
              Content: pageData.content,
              _isDraft: true // 标记为草稿
            }
          }
        } else {
          pageData.draftNote = null
        }

        pageData.loading = true
        pageData.radiovalue = val
        getlist()
      })
    }

    function initOriginalState() {
      pageData.originalTitle = (pageData.currentNote.Title || '').trim()
      pageData.originalContent = (pageData.content || '').trim()
      pageData.originalDate = pageData.selectedDate ? pageData.selectedDate.format('YYYY-MM-DD') : ''
    }

    function hasUnsavedChanges() {
      // 如果列表为空，说明没有数据，不需要检测
      if (pageData.noteslist.length === 0) return false

      const currentTitle = (pageData.currentNote.Title || '').trim()
      const currentContent = (pageData.content || '').trim()
      const currentDate = pageData.selectedDate ? pageData.selectedDate.format('YYYY-MM-DD') : ''

      return currentTitle !== pageData.originalTitle ||
        currentContent !== pageData.originalContent ||
        currentDate !== pageData.originalDate
    }

    function checkUnsavedChange(nextAction: () => void) {
      if (hasUnsavedChanges()) {
        Modal.confirm({
          content: tran('检测到未保存的更改，离开页面将丢失所有修改。确定要继续吗？'),
          okText: tran('离开'),
          cancelText: tran('取消'),
          onOk() {
            // 恢复原始状态
            if (pageData.currentNote) {
              pageData.currentNote.Title = pageData.originalTitle
              pageData.content = pageData.originalContent
              pageData.currentNote.Content = pageData.originalContent

              // 如果是新建笔记，确保列表中的对应项也被重置
              if (!pageData.currentNote.ID) {
                const newItem = pageData.noteslist.find((item: any) => !item.ID)
                if (newItem) {
                  newItem.Title = pageData.originalTitle
                  newItem.Content = pageData.originalContent
                }
              }

              if (pageData.originalDate) {
                pageData.selectedDate = window.moment(pageData.originalDate)
              }
            }
            nextAction()
          }
        })
      } else {
        nextAction()
      }
    }

    async function getlist(targetId?: string | number, tradeDay?: number) {
      pageData.loading = true
      const data: any = {}
      console.log('route.query.showTradeDay', route.query.showTradeDay)
      // 构造请求参数
      // radiovalue=1 时始终用 1，radiovalue=4 时根据 showTradeDay 决定用 3 还是 4
      const obj: any = {
        // Tab: pageData.radiovalue === 1 ? 1 : (route.query.showTradeDay ? 3 : pageData.radiovalue),
        Tab: pageData.radiovalue,
        Uid: defaultUid,
        ProjectID: defaultProjectID,
        PageID: 1,
        PageSize: 1000
      }

      // 如果路由有 showTradeDay 参数，解析并设置 TradeDay
      if (route.query.showTradeDay) {
        const dateStr = route.query.showTradeDay as string
        if (dateStr.length === 8) {
          // 格式 YYYYMMDD，解析为时间戳
          const year = dateStr.substring(0, 4)
          const month = dateStr.substring(4, 6)
          const day = dateStr.substring(6, 8)
          const momentDate = window.moment(`${year}-${month}-${day}`)
          if (momentDate.isValid()) {
            obj.TradeDay = Number(momentDate.format('X'))
            // 同步更新选中日期
            pageData.selectedDate = momentDate
            if (!momentDate.isAfter(window.moment(), 'day')) {
              pageData.lastValidDate = momentDate.clone()
            }
          }
        }
      } else if (tradeDay) {
        // 使用传入的 tradeDay 参数
        obj.TradeDay = tradeDay
      }

      // 个股相关 (Tab=4 或 Tab=3) 时，添加 Stock 参数
      if (pageData.radiovalue === 4 || route.query.showTradeDay) {
        obj.Stock = [
          {
            Exch: route.query.market,
            Inst: route.query.code
          }
        ]
      }

      data.data = obj
      console.log('我要刷新接口, obj:', obj)
      data.headers = { token: pageData.token }

      return stocknotesApi.getMsgboardNoteList(data).then((res: any) => {
        let { Data } = res
        // 如果是繁体，转换数据
        if (isTraditional && Data) {
          Data = objTran('t', Data)
        }
        if (Data) {
          pageData.noteslist = Data.sort((a: any, b: any) => b.PubTime - a.PubTime)

          // 如果指定了目标ID，优先选中该目标
          if (targetId) {
            const exists = pageData.noteslist.find((item: any) => item.ID === targetId)
            if (exists) {
              getNoteDetail(exists.ID)
              return
            }
          }

          // 如果路由有 showTradeDay 参数，匹配列表中的笔记
          if (route.query.showTradeDay) {
            const dateStr = route.query.showTradeDay as string
            if (dateStr.length === 8) {
              const year = dateStr.substring(0, 4)
              const month = dateStr.substring(4, 6)
              const day = dateStr.substring(6, 8)
              const momentDate = window.moment(`${year}-${month}-${day}`)
              if (momentDate.isValid()) {
                const tradeDayTimestamp = Number(momentDate.format('X'))
                // 查找匹配 TradeDay 的笔记
                console.log('pageData.noteslist', pageData.noteslist)
                const matchedNote = pageData.noteslist.find((item: any) => item.TradeDay === tradeDayTimestamp)
                if (matchedNote) {
                  getNoteDetail(matchedNote.ID)
                  // 滚动到对应位置
                  nextTick(() => {
                    scrollToNote(matchedNote.ID)
                  })
                  return
                }
              }
            }
          }

          // 如果有暂存的草稿笔记，将其插入到列表最前面
          if (pageData.draftNote) {
            // 只有当切换回原来的 Tab 上下文（比如有相同的 stock 或者全部笔记）时，才恢复？
            // 用户需求是：新增笔记 -> 切全部 -> 切回个股 -> 恢复
            // 所以我们简化处理：只要有 draftNote，就尝试恢复显示

            // 检查当前环境是否适合恢复 draftNote（例如个股笔记草稿只在个股 Tab 或全部 Tab 显示）
            // 这里暂时不做严格校验，直接插回去让用户看到
            pageData.noteslist.unshift(pageData.draftNote)
            pageData.currentNote = pageData.draftNote
            pageData.content = pageData.draftNote.Content
            pageData.draftNote = null // 恢复后清空
          } else if (pageData.isFirstLoad) {
            // 首次加载时，默认打开新建笔记
            pageData.isFirstLoad = false
            setTimeout(() => {
              addEmptyNote(true)
            }, 100)
          } else if (pageData.noteslist.length > 0) {
            setTimeout(() => {
              getNoteDetail(pageData.noteslist[0].ID)
            }, 100)
          } else {
            pageData.currentNote = { index: -1 }
            pageData.content = ''
          }
        } else {
          pageData.noteslist = []
          if (pageData.draftNote) {
            pageData.noteslist.unshift(pageData.draftNote)
            pageData.currentNote = pageData.draftNote
            pageData.content = pageData.draftNote.Content
            pageData.draftNote = null
          } else if (pageData.isFirstLoad) {
            // 首次加载且列表为空时，也打开新建笔记
            pageData.isFirstLoad = false
            setTimeout(() => {
              addEmptyNote(true)
            }, 100)
          } else {
            pageData.currentNote = { index: -1 }
            pageData.content = ''
          }
        }
      }).finally(() => {
        pageData.loading = false
        // 首次加载完成后触发渐入动画
        if (!pageData.pageLoaded) {
          nextTick(() => {
            pageData.pageLoaded = true
          })
        }
      })
    }

    // 滚动到指定笔记位置
    function scrollToNote(noteId: string | number) {
      nextTick(() => {
        const listContent = document.querySelector('.list-content ul')
        const noteIndex = pageData.noteslist.findIndex((item: any) => item.ID === noteId)
        if (listContent && noteIndex >= 0) {
          const noteItems = listContent.querySelectorAll('li')
          if (noteItems[noteIndex]) {
            noteItems[noteIndex].scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      })
    }

    function getNoteDetail(id: number | string | undefined) {
      if (!id) {
        pageData.currentNote = { index: 0 }
        pageData.content = ''
        return
      }
      const data: any = {}
      data.data = { ID: id }
      data.headers = { token: pageData.token }

      stocknotesApi.getMsgboardNote(data).then((res: any) => {
        let { Data } = res
        // 如果是繁体，转换数据
        if (isTraditional && Data) {
          Data = objTran('t', Data)
        }
        pageData.currentNote = Data || {}
        // 优先使用 TradeDay，如果没有则使用 PubTime
        if (pageData.currentNote.TradeDay) {
          pageData.selectedDate = window.moment(pageData.currentNote.TradeDay * 1000)
          if (!pageData.selectedDate.isAfter(window.moment(), 'day')) {
            pageData.lastValidDate = pageData.selectedDate.clone()
          }
        } else if (pageData.currentNote.PubTime) {
          pageData.selectedDate = window.moment(pageData.currentNote.PubTime * 1000)
          if (!pageData.selectedDate.isAfter(window.moment(), 'day')) {
            pageData.lastValidDate = pageData.selectedDate.clone()
          }
        }
        pageData.dateChanged = false
        pageData.currentNote.index = pageData.noteslist.findIndex((item: any) => item.ID === pageData.currentNote.ID)
        pageData.content = handlecontent(Data.Content || '')
        initOriginalState()
      })
    }

    function addEmptyNote(skipUnsavedCheck: boolean | MouseEvent = false) {
      // 如果是 MouseEvent（从模板点击触发），转换为 false
      const shouldSkip = typeof skipUnsavedCheck === 'boolean' ? skipUnsavedCheck : false
      const doAddNote = () => {
        if (!route.query.token) {
          stock.gotoClientLogin()
          return
        }

        // 检查是否存在未保存的笔记（ID为空）
        const unsavedNote = pageData.noteslist.find((item: any) => !item.ID)
        if (unsavedNote) {
          message.warning(tran('请完善当前笔记'))
          getCurrentNote(unsavedNote)
          return
        }

        const data: any = {
          Uid: defaultUid,
          ProjectID: defaultProjectID,
          ProjectName: defaultProjectName,
          Exch: route.query.market,
          Inst: route.query.code,
          Title: '',
          Content: '',
          TradeDay: today,
          PubTime: today,
          ID: '',
          index: 0
        }

        pageData.currentNote = data
        pageData.content = ''

        // 清空光标信息
        pageData.anchorNode = null
        pageData.selection = null
        pageData.selectionIndex = 0

        if (data.PubTime) {
          pageData.selectedDate = window.moment(data.PubTime * 1000)
          if (!pageData.selectedDate.isAfter(window.moment(), 'day')) {
            pageData.lastValidDate = pageData.selectedDate.clone()
          }
        }
        pageData.dateChanged = false
        pageData.noteslist.unshift(data)
        initOriginalState()

        // 新建笔记后，将光标焦点定位到标题输入框
        nextTick(() => {
          const titleInput = document.querySelector('.title-input') as HTMLInputElement
          if (titleInput) {
            titleInput.focus()
          }
        })

        // 如果有股票代码和名称，直接从路由参数拼接插入（不调接口）
        if (route.query.code && route.query.market && route.query.name) {
          const code = route.query.code as string
          const market = route.query.market as string
          const name = decodeURIComponent(route.query.name as string)
          const tag = `$-${name}(${code}.${market})-$`
          insertAtCursor(tag)

          // 自动插入后，更新原始状态，避免误报
          nextTick(() => {
            nextTick(() => {
              initOriginalState()
            })
          })
        }
      }

      // 首次加载时跳过未保存检查
      if (shouldSkip) {
        doAddNote()
      } else {
        checkUnsavedChange(doAddNote)
      }
    }



    function getCurrentNote(item: note) {
      if (pageData.currentNote === item) return;
      if (pageData.currentNote.ID === item.ID && item.ID) return;

      const performSwitch = () => {
        if (!item.ID) {
          pageData.currentNote = item
          pageData.content = item.Content || ''

          // 切换笔记时，必须清空光标历史，否则 insertAtCursor 会操作错误的 DOM
          pageData.anchorNode = null
          pageData.selection = null
          pageData.selectionIndex = 0

          if (item.PubTime) {
            pageData.selectedDate = window.moment(item.PubTime * 1000)
            if (!pageData.selectedDate.isAfter(window.moment(), 'day')) {
              pageData.lastValidDate = pageData.selectedDate.clone()
            }
          }
          pageData.dateChanged = false

          // 如果内容为空且有股票代码，自动查询并插入股票信息

          initOriginalState()
        } else {
          getNoteDetail(item.ID)
          if (item.Exch && item.Inst) {
            //  fetchTradeDays(item.Exch, item.Inst)
          }
        }
      }

      checkUnsavedChange(performSwitch)
    }

    function handleSearch(val: any) {
      if (!val) {
        pageData.options = []
        return
      }
      const data: any = {}
      data.data = {
        // 市场类别：沪深股 + 港股 + 美股 + 期货
        // 为空代表全部，这里显式限制到四大类
        markettype: ['CN', 'HK', 'US', 'FU'],
        // 具体市场：沪深（含ETF）、北交所、港股、美股、期货/指数
        marketcode: 'HKSE,NASDAQ,NYSE,AMEX,HKINDEX,USINDEX,shetf,szetf,sh,sz,nsb,USSECTOR,HKSECTOR,HKEX,AHZSECTOR,COMEX,HKQZ,SGX,FHSI,HKIDX,NASIDX,CME',
        key: val,          // 接口本身支持名称/代码/拼音首字母

        active: true
      }
      data.headers = { 'Content-Type': 'application/x-www-form-urlencoded' }

      allApi.searchStock(data).then((res: any) => {
        let list = res.Data || []
        const priority: Record<string, number> = {
          // 港股优先
          HKSE: 1,
          HKINDEX: 1,
          // 沪深北 + ETF
          sh: 2,
          sz: 2,
          bj: 2,
          shetf: 2,
          szetf: 2,
          // 美股
          NASDAQ: 3,
          NYSE: 3,
          AMEX: 3,
        }

        list.sort((a: any, b: any) => {
          const pA = priority[a.MarketCode] || 99
          const pB = priority[b.MarketCode] || 99
          return pA - pB
        })

        pageData.options = list
      })
    }

    function handleChange(val: any) {
      const tag = `$-${val.Name}(${val.Code}.${val.MarketCode})-$`
      insertAtCursor(tag)
      pageData.showSelect = false
      pageData.searchText = ''
      pageData.options = []
    }

    function handleDateChange(date: any, dateString: string) {
      if (dateString) {
        const tag = `$-${dateString}-$`
        insertAtCursor(tag)
      }
    }

    function onDateSelect(date: any) {
      if (date) {
        const dateString = date.format('YYYY-MM-DD')
        // Verify if disabled? a-calendar should prevent selecting disabled dates but let's be safe
        if (handleDateDisabled(date)) {
          return
        }
        pageData.selectedDate = date
        pageData.dateChanged = true
        // const tag = `$-${dateString}-$`
        // insertAtCursor(tag)
        pageData.showDateSelect = false
      }
    }

    function insertAtCursor(text: string) {
      // Restore original logic for insertion at cursor
      let str = ''
      const editorElem = document.querySelector('.editwrap div') // Divedit root
      const editwrap: any = editorElem?.childNodes

      // 校验 anchorNode 是否属于编辑器，如果不属于，强制走追加模式
      let isValidAnchor = false
      if (pageData.anchorNode && pageData.anchorNode.nodeValue && editorElem && editorElem.contains(pageData.anchorNode)) {
        isValidAnchor = true
      }

      // Fallback if no anchorNode or logic fails
      if (!isValidAnchor) {
        pageData.content += text
        nextTick(() => {
          pageData.content = handlecontent(pageData.content)
        })
        return
      }

      let anchorvalue = (((pageData.anchorNode || {}).previousElementSibling || {}).outerHTML || '') + (pageData.anchorNode || {}).nodeValue || ''

      if ((pageData.anchorNode || {}).nodeValue) {
        Object.keys(editwrap).forEach((item: any) => {
          if ((editwrap[item].nodeValue || editwrap[item].innerText) === pageData.anchorNode.nodeValue) {
            anchorvalue = editwrap[item].nodeValue || editwrap[item].innerText
            str += anchorvalue.slice(0, pageData.selectionIndex) + text + anchorvalue.slice(pageData.selectionIndex)
          } else {
            str += editwrap[item].outerHTML || editwrap[item].nodeValue || editwrap[item].innerText
          }
        })
      } else {
        str += pageData.content + text
      }

      nextTick(() => {
        pageData.content = handlecontent(str)
        if (pageData.currentNote) {
          pageData.currentNote.Content = pageData.content
        }
      })
    }

    function escapeRegExp(string: string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // 构建表情正则（只构建一次）
    const allEmojis = [...emojiArray, ...emojiArrayV2];
    allEmojis.sort((a, b) => b.length - a.length); // 长代码优先
    const emojiPattern = allEmojis.map(escapeRegExp).join('|');
    const customEmojiReg = new RegExp(emojiPattern, 'g');

    function handlecontent(content: string) {
      let str = content || ''
      // 1. 股票代码替换（保持字符串替换，因为它生成特定 HTML 结构）
      // 判断是否需要禁用沪深股票
      str = str.replace(/\$-([^\(]+)\(([^.]+)\.([^)]+)\)-\$/g, (match, name, code, market) => {
        const marketLower = (market || '').toLowerCase()
        const isHuShen = marketLower === 'sh' || marketLower === 'sz'
        const disabled = isFangDeZhiTou.value && isHuShen
        const className = disabled ? 'contentstock disabled' : 'contentstock'
        return `<span class="${className}" contenteditable=false>${name}(${code}.${market})</span>`
      })
      str = str.replace(/\$-(\d{4}-\d{2}-\d{2})-\$/g, '<span class="contentstock" contenteditable=false>$1</span>')
      str = str.replace(/\$-/g, '<span class="contentstock" contenteditable=false>$')
      str = str.replace(/-\$/g, '$</span> ')

      // 2. 表情替换 - 使用 DOM 解析防止破坏现有 HTML 结构（特别是 img 标签的属性）
      try {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = str

        walkAndReplace(tempDiv)

        return tempDiv.innerHTML
      } catch (e) {
        console.error('Emoji parse error:', e)
        return str
      }
    }

    function walkAndReplace(node: Node) {
      if (node.nodeType === 3) { // Node.TEXT_NODE
        const text = node.nodeValue || '';
        // 尝试替换表情
        const replacedHtml = text.replace(customEmojiReg, (r) => {
          if (emojiArray.indexOf(r) > -1) {
            const index = emojiArray.indexOf(r)
            const src = getEmojiUrl(index)
            return `<img class="emojiMsg" code="${r}" src="${src}" oncontextmenu="return false"/>`
          } else if (emojiArrayV2.indexOf(r) > -1) {
            const index = emojiArrayV2.indexOf(r)
            const src = getEmojiV2Url(index)
            return `<img class="emojiMsg" code="${r}" src="${src}" oncontextmenu="return false"/>`
          }
          return r
        });

        // 如果文本发生了变化（说明包含表情代码）
        if (replacedHtml !== text) {
          const span = document.createElement('span');
          span.innerHTML = replacedHtml;
          const parent = node.parentNode;
          if (parent) {
            // 将生成的节点插入到原文本节点前
            while (span.firstChild) {
              parent.insertBefore(span.firstChild, node);
            }
            // 移除原文本节点
            parent.removeChild(node);
          }
        }
      } else if (node.nodeType === 1) { // Node.ELEMENT_NODE
        const el = node as Element;
        // 跳过脚本、样式表和已生成的表情图片
        if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.classList.contains('emojiMsg')) {
          return;
        }
        // 递归遍历子节点（使用 Array.from 创建副本，因为遍历过程中可能会修改 DOM 结构）
        Array.from(node.childNodes).forEach(child => walkAndReplace(child));
      }
    }

    function changecontent(content: string) {
      if (typeof content === 'string') {
        pageData.content = content
        pageData.selectionIndex = pageData.content.length
        if (pageData.currentNote) {
          pageData.currentNote.Content = content
        }
      }
    }

    function markEdited() {
    }

    function submitNote() {
      if (!pageData.currentNote.Title || !pageData.currentNote.Title.trim()) {
        message.error(tran('请输入标题'))
        return
      }

      if (pageData.currentNote.Title.length > 60) {
        message.error(tran('标题不能超过60个字，当前') + pageData.currentNote.Title.length + tran('个字'))
        return
      }

      const plainText = stripHtml(pageData.content).trim()
      const hasImage = pageData.content.includes('<img')

      if (!plainText && !hasImage) {
        message.error(tran('请输入内容'))
        return
      }

      const contentToSave = restoreContent(pageData.content)
      if (contentToSave.length > 2048) {
        message.error(tran('内容不能超过2048个字，当前') + contentToSave.length + tran('个字'))
        return
      }

      if (pageData.currentNote.ID) {
        updateNote()
      } else {
        addNote()
      }
    }

    function restoreContent(content: string) {
      if (!content) return ''
      try {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = content

        // 1. 还原股票标签：<span class="contentstock">name(code.market)</span> -> $-name(code.market)-$
        const stocks = tempDiv.querySelectorAll('.contentstock')
        stocks.forEach(span => {
          const text = span.textContent || ''
          // 还原为 $-...-$ 格式
          const textNode = document.createTextNode(`$-${text}-$`)
          span.parentNode?.replaceChild(textNode, span)
        })

        // 2. 还原表情图片
        const imgs = tempDiv.querySelectorAll('img.emojiMsg')
        imgs.forEach(img => {
          const code = img.getAttribute('code')
          if (code) {
            const textNode = document.createTextNode(code)
            img.parentNode?.replaceChild(textNode, img)
          }
        })
        return tempDiv.innerHTML
      } catch (e) {
        return content
      }
    }

    function updateNote() {
      const data: any = {}
      data.data = {
        Uid: defaultUid,
        ProjectID: defaultProjectID,
        ProjectName: defaultProjectName,
        Title: pageData.currentNote.Title,
        Content: restoreContent(pageData.content),
        ID: pageData.currentNote.ID,
        TradeDay: pageData.dateChanged && pageData.selectedDate ? Number(pageData.selectedDate.format('X')) : (pageData.currentNote.TradeDay || today)
        // PubTime: Date.now() / 1000
      }
      data.headers = { token: pageData.token }

      stocknotesApi.getMsgboardUpdateNote(data).then((res: any) => {
        const { Code, Data } = res
        if (Code === 0) {
          // 保存成功后，重新获取列表并保持选中当前笔记
          getlist(pageData.currentNote.ID)
          message.success('保存成功')
          initOriginalState() // 保存成功后更新原始状态

          // 保存成功后，调用客户端指令刷新外部的 StockNote
          const url = `gw://funccall?funcname=OnFireCommand;Command=RefreshStockNote`
          window.open(url)
          console.log('调用客户端指令刷新外部的 保存成功')
        }
      })
    }

    function addNote() {
      const data: any = {}
      data.data = {
        Uid: defaultUid,
        ProjectID: defaultProjectID,
        ProjectName: defaultProjectName,
        Exch: route.query.market,
        Inst: route.query.code,
        Title: pageData.currentNote.Title,
        Content: restoreContent(pageData.content),
        TradeDay: pageData.selectedDate ? Number(pageData.selectedDate.format('X')) : today
      }
      data.headers = { token: pageData.token }

      stocknotesApi.getMsgboardCreateNote(data).then((res: any) => {
        const { Code } = res
        if (Code === 0) {
          getlist()
          message.success('新建成功')

          // 新建成功后，调用客户端指令刷新外部的 StockNote
          if (pageData.currentProject) {
            const url = `gw://funccall?funcname=OnFireCommand;Command=RefreshStockNote`
            window.open(url)
            console.log('调用客户端指令刷新外部的 新建成功')
          }
        }
      })
    }

    function onRightClick(item: any, e: any) {
    }

    function deleteNoteById(item: any) {
      Modal.confirm({
        content: '删除该笔记后不可恢复！',
        okText: '删除',
        onOk() {
          if (!item.ID) {
            // 本地删除未保存的笔记
            const index = pageData.noteslist.indexOf(item)
            if (index > -1) {
              pageData.noteslist.splice(index, 1)
              // 删除后选中第一个笔记
              if (pageData.noteslist.length > 0) {
                getNoteDetail(pageData.noteslist[0].ID)
              } else {
                pageData.currentNote = { index: -1 }
                pageData.content = ''
              }
              // 重置原始状态，避免切换笔记时误报有未保存更改
              initOriginalState()
              message.success('删除成功')

            }
          } else {
            performDelete(item.ID)
          }
        }
      })
    }

    function performDelete(id: string) {
      const data: any = {}
      data.data = {
        ID: id,
        Uid: route.query.uid,
        ProjectID: route.query.ProjectID
      }
      data.headers = { token: pageData.token }

      stocknotesApi.getMsgboardDeleteNote(data).then((res: any) => {
        if (res.Code === 0) {
          message.success('删除成功')
          // 从本地列表中移除已删除的笔记
          const deleteIndex = pageData.noteslist.findIndex((item: any) => item.ID === id)
          if (deleteIndex > -1) {
            pageData.noteslist.splice(deleteIndex, 1)
          }
          // 选中第一个笔记
          if (pageData.noteslist.length > 0) {
            getNoteDetail(pageData.noteslist[0].ID)
          } else {
            pageData.currentNote = { index: -1 }
            pageData.content = ''
          }

          // 删除成功后，调用客户端指令刷新外部的 StockNote
          const url = `gw://funccall?funcname=OnFireCommand;Command=RefreshStockNote`
          window.open(url)
          console.log('调用客户端指令刷新外部的 删除成功后，调用客户端指令刷新外部的')
        }
      })
    }

    function execBold() {
      document.execCommand('bold')
    }

    function getGb() {
      const selection: any = getSelection()
      pageData.selection = JSON.parse(JSON.stringify(selection))
      pageData.anchorNode = selection.anchorNode
      pageData.selectionIndex = selection.baseOffset
    }

    function stripHtml(html: string) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      const txt = tmp.textContent || tmp.innerText || '';
      return txt.length > 20 ? txt.slice(0, 20) + '...' : txt;
    }

    function getMarketName(code: string) {
      const map: any = {
        // 沪深北
        sh: '沪A',
        sz: '深A',
        bj: '北A',
        // 港股相关
        HKSE: '港股',
        HKINDEX: '港股',
        HKIDX: '港股',
        HKSECTOR: '港股',
        HKEX: '港股',
        HKQZ: '港股',
        hk: '港股',
        // 美股相关
        NASDAQ: '美股',
        NYSE: '美股',
        AMEX: '美股',
        USINDEX: '美股',
        USSECTOR: '美股',
        NASIDX: '美股',
        us: '美股',
        // 基金（ETF + 场外）
        shetf: '基金',
        szetf: '基金',
        AHZSECTOR: 'A股板块',
        // 期货 / 衍生品
        COMEX: '期货',
        SGX: '期货',
        FHSI: '期货',
        CME: '期货',
        // 其他
        nsb: '新三板',
      }
      return map[code] || code
    }

    function getEmojiUrl(index: number) {
      return `https://upload.techgp.cn/production/im/emoji/${index}.png`
    }

    function getEmojiV2Url(index: number) {
      let name
      if (index < 9) {
        name = 'ic_emoji_0' + (index + 1)
      } else {
        name = 'ic_emoji_' + (index + 1)
      }
      return `https://upload.techgp.cn/production/static/article/${name}.png`
    }

    function handleEmojiSelect(item: any, type: 'basic' | 'v2', index: number) {
      let imgTag = ''
      if (type === 'basic') {
        const code = emojiArray[index]
        const src = getEmojiUrl(index)
        imgTag = `<img class="emojiMsg" code="${code}" src="${src}" oncontextmenu="return false" style="width:24px;height:24px;vertical-align:middle;margin:0 2px;"/>`
      } else {
        const code = emojiArrayV2[index]
        const src = getEmojiV2Url(index)
        imgTag = `<img class="emojiMsg" code="${code}" src="${src}" oncontextmenu="return false" style="width:24px;height:24px;vertical-align:middle;margin:0 2px;"/>`
      }
      insertAtCursor(imgTag)
      pageData.showEmojiSelect = false
    }

    watch(
      () => route.query.theme,
      (val: any) => {
        pageData.theme = val as string
      }
    )

    watch(() => pageData.selectedDate, (newVal, oldVal) => {
      if (newVal) {
        // 如果选中了未来时间
        if (newVal.isAfter(window.moment(), 'day')) {
          message.warn({
            content: tran('未来日期不可选'),
            top: '20px',
            duration: 2,
            class: 'message-right'
          })
          nextTick(() => {
            if (pageData.lastValidDate) {
              pageData.selectedDate = pageData.lastValidDate.clone()
            } else {
              pageData.selectedDate = window.moment()
            }
          })
        } else {
          // 有效日期，更新记录
          // 注意：这里我们只记录非未来日期为有效，不考虑是否是节假日（因为用户只提到了未来时间）
          // 如果需要严格限制节假日，也可以在这里加判断
          pageData.lastValidDate = newVal.clone()
        }
      }
    })

    return {
      ...toRefs(pageData),
      tran,
      addEmptyNote,
      handleSearch,
      handleChange,
      handleDateChange,
      handleTabChange,
      changecontent,
      submitNote,
      getGb,
      getCurrentNote,
      deleteNoteById,
      onRightClick,
      execBold,
      moment: window.moment,
      getMarketName,
      stripHtml,
      diveditRef,
      markEdited,
      handleDateDisabled,
      onDateSelect,
      isRestDay,
      emojis,
      emojiArrayV2,
      getEmojiUrl,
      getEmojiV2Url,
      handleEmojiSelect,
      isFangDeZhiTou
    }
  }
})
